import React, { useCallback } from 'react';

import { HooksDisplayGrid } from 'ui-components/components/hooks-display-grid';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import {
  ActivateReturnsGridPlaceholder,
  NoPendingApprovalReturnsPlaceholder,
  NoPendingPaymentReturnsPlaceholder,
  NoCompletedReturnsPlaceholder,
  NoNeedsAttentionReturnsPlaceholder,
  NoSearchResultsGridPlaceholder,
  NoFilterResultsGridPlaceholder,
} from './placeholders';
import { emptyArray } from 'ui-components/default-props/empty';
import { EnhancedReturnsFilterBar } from './filter-bar';
import { PagingControl } from '../../../../components/paging-control';
import { useEnhancedReturnsContext } from './enhanced-returns-context';
import {
  STATUS_PENDING_APPROVAL,
  STATUS_PENDING_PAYMENT,
  NEEDS_ATTENTION,
  STATUS_COMPLETED,
} from '../../../../store/returns/returns.helpers';

const mapConfiguration = (columns, columnsConfiguration) => {
  const columnMap = columns.reduce((acc, column) => {
    const id = column.id;
    return {
      ...acc,
      [id]: column,
    };
  }, {});

  return columnsConfiguration.reduce((acc, config) => {
    const column = columnMap[config.id];

    if (column) {
      acc.push({
        ...config,
        ...column,
      });
    }

    return acc;
  }, []);
};

const Placeholder = ({ isLoading, isStoreSetupCompleteForEnhancedReturns, hasReturns, isSearchMode, statusViewId, hasFilters }) => {
  if (isLoading) {
    return <LoadingIndicator />;
  }

  if (!isStoreSetupCompleteForEnhancedReturns) {
    return <ActivateReturnsGridPlaceholder />;
  }

  if (hasReturns) {
    return null;
  }

  if (hasFilters) {
    return <NoFilterResultsGridPlaceholder />;
  }

  if (isSearchMode) {
    return <NoSearchResultsGridPlaceholder />;
  }

  switch (statusViewId) {
    case STATUS_PENDING_APPROVAL:
      return <NoPendingApprovalReturnsPlaceholder />;
    case STATUS_PENDING_PAYMENT:
      return <NoPendingPaymentReturnsPlaceholder />;
    case NEEDS_ATTENTION:
      return <NoNeedsAttentionReturnsPlaceholder />;
    case STATUS_COMPLETED:
      return <NoCompletedReturnsPlaceholder />;
    default:
      return null;
  }
};

export const EnhancedReturnsBaseGrid = ({ columns = emptyArray, viewSpecificFilters = emptyArray }) => {
  const {
    returns,
    isLoading,
    totalRecords,
    page,
    pageSize,
    updatePageInformation,
    totalPages,
    viewStartingCount,
    viewEndingCount,
    isStoreSetupCompleteForEnhancedReturns,
    columnsConfig,
    updateColumnsConfig,
    isSearchMode,
    statusViewId,
    filters,
  } = useEnhancedReturnsContext();
  const [selectedCount, setSelectedCount] = useAsyncSafeState(0);

  const hasFilters = !!filters && Object.keys(filters).length > 0;

  const onPageChange = useCallback(
    (newPage, newPageSize) => {
      updatePageInformation(newPage, newPageSize);
    },
    [updatePageInformation],
  );

  const onSelectedChanged = useCallback(
    (selected) => {
      setSelectedCount(selected.length);
    },
    [setSelectedCount],
  );

  const onColumnsChanged = useCallback(
    (config) => {
      updateColumnsConfig(config);
    },
    [updateColumnsConfig],
  );

  const onOrderByChanged = useCallback(
    (config) => {
      updateColumnsConfig(config);
    },
    [updateColumnsConfig],
  );

  return (
    <>
      <EnhancedReturnsFilterBar viewSpecificFilters={viewSpecificFilters} />
      <HooksDisplayGrid
        columns={mapConfiguration(columns, columnsConfig)}
        data={isLoading ? emptyArray : returns}
        getColumnId={(column) => column.id}
        onColumnsChanged={onColumnsChanged}
        onOrderByChanged={onOrderByChanged}
        onSelectedChanged={onSelectedChanged}
        placeholder={
          <Placeholder
            hasFilters={hasFilters}
            hasReturns={returns.length > 0}
            isLoading={isLoading}
            isSearchMode={isSearchMode}
            isStoreSetupCompleteForEnhancedReturns={isStoreSetupCompleteForEnhancedReturns}
            statusViewId={statusViewId}
          />
        }
        renderCheckbox={false}
      />
      <PagingControl
        numberSelected={selectedCount}
        onPageChange={onPageChange}
        page={page}
        pageSize={pageSize}
        totalPages={totalPages}
        totalRecords={totalRecords}
        viewEndingCount={viewEndingCount}
        viewStartingCount={viewStartingCount}
      />
    </>
  );
};
